/**
 * Parabolic SAR (Stop and Reverse)
 *
 * Trailing stop indicator that accelerates as the trend extends.
 * Plots dots above price in a downtrend and below price in an uptrend.
 * Flips position when price crosses the SAR value.
 * Default: start=0.02, increment=0.02, max=0.2.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const parabolicSarIndicator: IndicatorDefinition = {
  id: "psar",
  name: "Parabolic SAR",
  shortName: "PSAR",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "afStart", label: "AF Start", type: "number", default: 0.02, min: 0.001, max: 0.1, step: 0.001 },
    { key: "afStep", label: "AF Step", type: "number", default: 0.02, min: 0.001, max: 0.1, step: 0.001 },
    { key: "afMax", label: "AF Max", type: "number", default: 0.2, min: 0.05, max: 0.5, step: 0.01 },
    { key: "color", label: "Color", type: "color", default: "#FFD600" },
    { key: "dotSize", label: "Dot Size", type: "number", default: 1, min: 1, max: 8, step: 1 },
  ],
  plots: [
    { key: "psar", label: "PSAR", color: "#FFD600", style: "circles", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { afStart, afStep, afMax } = params;
    if (candles.length < 2) return [{ key: "psar", data: [] }];

    const data: { time: number; value: number }[] = [];

    // Initialize
    let isLong = candles[1].close > candles[0].close;
    let sar = isLong ? candles[0].low : candles[0].high;
    let ep = isLong ? candles[1].high : candles[1].low;
    let af = afStart;

    data.push({ time: candles[0].time, value: sar });

    for (let i = 1; i < candles.length; i++) {
      const prevSar = sar;
      sar = prevSar + af * (ep - prevSar);

      if (isLong) {
        // Make sure SAR doesn't go above prior two lows
        if (i >= 2) {
          sar = Math.min(sar, candles[i - 1].low, candles[i - 2].low);
        } else {
          sar = Math.min(sar, candles[i - 1].low);
        }

        if (candles[i].low < sar) {
          // Reverse to short
          isLong = false;
          sar = ep;
          ep = candles[i].low;
          af = afStart;
        } else {
          if (candles[i].high > ep) {
            ep = candles[i].high;
            af = Math.min(af + afStep, afMax);
          }
        }
      } else {
        // Short position
        if (i >= 2) {
          sar = Math.max(sar, candles[i - 1].high, candles[i - 2].high);
        } else {
          sar = Math.max(sar, candles[i - 1].high);
        }

        if (candles[i].high > sar) {
          // Reverse to long
          isLong = true;
          sar = ep;
          ep = candles[i].high;
          af = afStart;
        } else {
          if (candles[i].low < ep) {
            ep = candles[i].low;
            af = Math.min(af + afStep, afMax);
          }
        }
      }

      data.push({ time: candles[i].time, value: sar });
    }

    return [{ key: "psar", data }];
  },
};
